import { Badge } from '@mantine/core';
import {
  IconCircleCheck,
  IconAlertTriangle,
  IconClockHour4,
  IconAlertOctagon
} from '@tabler/icons-react';
import { DisplayStatus, getStatusLabel } from '../../types/status';
import { AppTooltip } from './AppTooltip';

interface StatusBadgeProps {
  status: DisplayStatus | 'past_due';
  label?: string;
  tooltipLabel?: string;
}

const getStatusIcon = (status: DisplayStatus | 'past_due'): JSX.Element | null => {
  switch (status) {
    case 'approved':
      return <IconCircleCheck size={16} style={{ display: 'block' }} />;
    case 'needs_attention':
      return <IconAlertTriangle size={16} style={{ display: 'block' }} />;
    case 'pending':
      return <IconClockHour4 size={16} style={{ display: 'block' }} />;
    case 'past_due':
      return <IconAlertOctagon size={16} style={{ display: 'block' }} />;
    case 'not_submitted':
      return null;
    default:
      return <IconClockHour4 size={16} style={{ display: 'block' }} />;
  }
};

const getCustomColor = (status: DisplayStatus | 'past_due', theme: any): string => {
  const colors: Record<DisplayStatus | 'past_due', string> = {
    not_submitted: theme.colors.gray[6],
    pending: theme.colors.yellow[6],
    in_review: theme.colors.yellow[6],
    approved: theme.colors.teal[6],
    needs_attention: theme.colors.red[6],
    past_due: theme.colors.red[6]
  };
  return colors[status];
};

export function StatusBadge({ status, label, tooltipLabel }: Readonly<StatusBadgeProps>) {
  const icon = getStatusIcon(status);
  const displayLabel = label ?? getStatusLabel(status);
  const fallbackTooltipLabel =
    displayLabel === 'Snapshot'
      ? 'No goal tracked for this metric.'
      : displayLabel === 'At risk'
        ? 'Performance is more than 10% behind goal.'
        : displayLabel === 'Watch'
          ? 'Performance is within 10% of goal pace.'
        : null;
  const resolvedTooltipLabel = tooltipLabel ?? fallbackTooltipLabel;

  const badge = (
    <Badge
      size="sm"
      variant="light"
      leftSection={icon}
      sx={(theme) => ({
        minWidth: 'auto',
        textAlign: 'center',
        padding: '8px 12px',
        height: 'auto',
        backgroundColor: theme.fn.rgba(getCustomColor(status, theme), 0.15),
        color: getCustomColor(status, theme),
        '.mantine-Badge-leftSection': {
          marginRight: 4,
          display: 'flex',
          alignItems: 'center',
          color: getCustomColor(status, theme)
        }
      })}
      aria-label={`Status: ${displayLabel}`}
    >
      {displayLabel}
    </Badge>
  );

  if (!resolvedTooltipLabel) {
    return badge;
  }

  return (
    <AppTooltip label={resolvedTooltipLabel} inline>
      <span>{badge}</span>
    </AppTooltip>
  );
} 